import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import VideoChatFrame from "./VideoChatFrame";
import CompanionStrip from "./CompanionStrip";
import { scrollToId } from "@/App";

const LINES = ["AI Characters.", "Real-Time Video.", "The Future of Entertainment."];

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.14 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const frameY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      <div className="pointer-events-none absolute -right-40 top-1/4 h-[560px] w-[560px] rounded-full bg-cyan-400/10 blur-[160px]" />
      <div className="pointer-events-none absolute -left-52 bottom-0 h-[420px] w-[420px] rounded-full bg-cyan-500/5 blur-[140px]" />

      <CompanionStrip />

      <div className="mx-auto grid w-full max-w-7xl flex-1 items-center gap-4 px-6 pb-8 pt-2 lg:grid-cols-2 lg:gap-16 lg:px-10">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-[11px] font-medium uppercase tracking-[0.35em] text-cyan-300/90"
          >
            AIROBOTCAMSTUDIO LTD · UK Company No. 17410621
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display mt-6 text-4xl font-light leading-[1.04] tracking-tighter max-[380px]:text-[2rem] sm:text-5xl lg:text-[64px]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 2 ? "text-glow-cyan font-medium text-cyan-300" : "text-white"}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="mt-5 max-w-md text-base leading-relaxed text-[#C5CBD5]"
          >
            We are developing a real-time AI video companion platform with photorealistic virtual
            characters, natural voice and live interaction — from AI chat to live presence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={() => scrollToId("technology")}
              className="group flex items-center gap-2 rounded-md bg-cyan-300 px-7 py-3.5 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-1 hover:bg-cyan-200"
            >
              Explore the Technology
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-investor-btn"
              onClick={() => scrollToId("investment")}
              className="rounded-md border border-cyan-300/30 px-7 py-3.5 text-sm font-semibold text-cyan-200/90 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-300/60 hover:text-cyan-100"
            >
              Investor Interest
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-4 text-[11px] uppercase tracking-[0.25em] text-[#B9A7FF] lg:mt-8"
          >
            MVP currently in development
          </motion.p>
        </motion.div>

        <motion.div
          style={{ y: frameY }}
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-auto w-full max-w-md lg:max-w-none"
        >
          <div className="pointer-events-none absolute -inset-8 rounded-[32px] bg-cyan-400/5 blur-2xl" />
          <VideoChatFrame
            src="/assets/hero_companion.jpg"
            name="ARIA"
            caption="Concept visual — AI companion experience in development"
          />
        </motion.div>
      </div>
    </section>
  );
}
